const TIMEOUT_MS = 3000; // 3 second timeout for API calls

// Helper function to add timeout to fetch
async function fetchWithTimeout(url, options = {}, timeout = TIMEOUT_MS) {
  const controller = new AbortController();
  const { signal } = controller;
  
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, { ...options, signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

export async function getCountry() {
    let countryName = 'Unknown'; // Default fallback
    
    // Try each service in order, stopping at the first that works
    const services = [
        // Service 1: ipdata.co
        async () => {
            try {
                const response = await fetchWithTimeout('https://api.ipdata.co?api-key=test');
                if (!response.ok) {
                    throw new Error(`Service returned ${response.status}`);
                }
                const data = await response.json();
                if (data && data.country_name) {
                    console.log('Country detected via ipdata.co:', data.country_name);
                    return data.country_name;
                }
                return null;
            } catch (error) {
                console.log('ipdata.co service failed:', error.message);
                return null;
            }
        },
        
        // Service 2: db-ip.com
        async () => {
            try {
                const response = await fetchWithTimeout('https://api.db-ip.com/v2/free/self');
                if (!response.ok) {
                    throw new Error(`Service returned ${response.status}`);
                }
                const data = await response.json();
                if (data && data.countryName) {
                    console.log('Country detected via db-ip.com:', data.countryName);
                    return data.countryName;
                }
                return null;
            } catch (error) {
                console.log('db-ip.com service failed:', error.message);
                return null;
            }
        },
        
        // Service 3: ipinfo.io
        async () => {
            try {
                const response = await fetchWithTimeout('https://ipinfo.io/json');
                if (!response.ok) {
                    throw new Error(`Service returned ${response.status}`);
                }
                const data = await response.json();
                if (data && data.country) {
                    const country = convertCountryCodeToName(data.country);
                    console.log('Country detected via ipinfo.io:', country);
                    return country;
                }
                return null;
            } catch (error) {
                console.log('ipinfo.io service failed:', error.message);
                return null;
            }
        }
    ];
    
    // Try each service in sequence
    for (const service of services) {
        const result = await service();
        if (result) {
            return result;
        }
    }
    
    // If all services failed, use a random country from a common list
    // This is better than "Unknown" for testing/development
    console.log('All geolocation services failed, using random country fallback');
    const fallbackCountries = [
        'United States', 'United Kingdom', 'Canada', 'Australia', 
        'Germany', 'France', 'Japan', 'Thailand', 'India'
    ];
    return fallbackCountries[Math.floor(Math.random() * fallbackCountries.length)];
}

// Simple function to convert country codes to full country names
export function convertCountryCodeToName(code) {
    if (!code) return 'Unknown';
    
    // If it already looks like a full country name (contains a space or is longer than 2 chars)
    if (code.includes(' ') || code.length > 2) {
        return code;
    }
    
    const countries = {
        'US': 'United States',
        'GB': 'United Kingdom',
        'CA': 'Canada',
        'AU': 'Australia',
        'TH': 'Thailand',
        'DE': 'Germany',
        'FR': 'France',
        'JP': 'Japan',
        'CN': 'China',
        'IN': 'India',
        'BR': 'Brazil',
        'MX': 'Mexico',
        'ES': 'Spain',
        'IT': 'Italy',
        'RU': 'Russia',
        'KR': 'South Korea',
        'NL': 'Netherlands',
        'SE': 'Sweden',
        'NO': 'Norway',
        'DK': 'Denmark',
        'FI': 'Finland',
        'PL': 'Poland',
        'SG': 'Singapore',
        'ID': 'Indonesia',
        'VN': 'Vietnam',
        'PH': 'Philippines',
        'MY': 'Malaysia',
        'NZ': 'New Zealand',
        'ZA': 'South Africa'
    };
    
    return countries[code] || code;
}